import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {getAccidentSurgeriesPath} from './PropertyPaths/accidentSurgeriesPath';

const MiscellaneousOutpatientSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'miscellaneous-outpatient-surgery-row-1'},
      items: [
        {
          key: 'miscellaneous-outpatient-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.incurralPeriod)}
              id={'miscellaneousOutpatientSurgeryDetails.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'miscellaneous-outpatient-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              adornment="money"
              disabled
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.benefitAmount)
              )}
              id={'miscellaneousOutpatientSurgeryDetails.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'miscellaneous-outpatient-surgery-row-2'},
      items: [
        {
          key: 'miscellaneous-outpatient-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMiscellaneousOutpatientSurgeryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.maxBenefitNumber)}
              disabled
              required
              id={'miscellaneousOutpatientSurgeryDetails.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'miscellaneous-outpatient-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCMiscellaneousOutpatientSurgeryBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.timePeriodCd)}
              disabled
              id={'accMiscellaneous.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const OpenAbdominalOrThoracicSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'open-abdominal-thoracic-surgery-row-1'},
      items: [
        {
          key: 'open-abdominal-thoracic-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.incurralPeriod)}
              id={'accAbdominThoratic.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'open-abdominal-thoracic-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.benefitAmount)
              )}
              disabled
              id={'accAbdominThoratic.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'open-abdominal-thoracic-surgery-row-2'},
      items: [
        {
          key: 'open-abdominal-thoracic-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCOpenAbdominalOrThoracicSurgeryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.maxBenefitNumber)}
              disabled
              required
              id={'accAbdominThoratic.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'open-abdominal-thoracic-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCXRayBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.timePeriodCd)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const LigamentRotatorcuffTendonKneecartilage = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-1'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.incurralPeriod)}
              disabled
              id={'accLigament.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-without-repair',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Without Repair"
              adornment="money"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.withoutRepairAmount)
              )}
              disabled
              id={'accLigament.withoutRepairAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-2'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-with-repair',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="With Repair"
              adornment="money"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.withRepairAmount)
              )}
              disabled
              id={'accLigament.withRepairAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCTendonLigamentRotatorCuffBenefit.maxBenefitNumber`}
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.maxBenefitNumber)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-2'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCTendonLigamentRotatorCuffBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.timePeriodCd)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const GeneralAnesthesia = () => {
  const {getQuoteFieldValue} = useQuoteField();
 const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'general-anesthesia-row-1'},
      items: [
        {
          key: 'general-anesthesia-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              required
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.incurralPeriod)}
              disabled
              id={'accAnesthesia.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'general-anesthesia-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.benefitAmount)
              )}
              disabled
              id={'accAnesthesia.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'general-anesthesia-row-2'},
      items: [
        {
          key: 'general-anesthesia-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCGeneralAnesthesiaBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.maxBenefitNumber)}
              disabled
              required
              id={'accAnesthesia.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'general-anesthesia-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCXRayBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.timePeriodCd)}
              disabled
              required
              id={'accAnesthesia.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const RupturedOrHerniatedDiscSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ruptured-or-herniated-disc-surgery-row-1'},
      items: [
        {
          key: 'ruptured-or-herniated-disc-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.incurralPeriod)}
              disabled
              required
              id={'accDiscSurgery.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ruptured-or-herniated-disc-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              adornment="money"
              required
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.benefitAmount))}
              disabled
              id={'accDiscSurgery.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ruptured-or-herniated-disc-surgery-row-2'},
      items: [
        {
          key: 'ruptured-or-herniated-disc-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCRupturedDiscBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.maxBenefitNumber)}
              disabled
              required
              id={'accDiscSurgery.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ruptured-or-herniated-disc-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCRupturedDiscBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.timePeriodCd)}
              disabled
              required
              id={'accDiscSurgery.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const BloodPlasmaPlatelets = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'blood-plasma-platelets-row-1'},
      items: [
        {
          key: 'blood-plasma-platelets-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.incurralPeriod)}
              disabled
              required
              id={'accBlood.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'blood-plasma-platelets-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.benefitAmount)
              )}
              disabled
              id={'accBlood.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'blood-plasma-platelets-row-2'},
      items: [
        {
          key: 'blood-plasma-platelets-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCBloodPlasmaPlateletsBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.maxBenefitNumber)}
              disabled
              required
              id={'accBlood.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'blood-plasma-platelets-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCBloodPlasmaPlateletsBenefit.timePeriodCd`}
              label="Time Period"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.timePeriodCd)}
              id={'accBlood.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const EyeInjury = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'eye-injury-row-1'},
      items: [
        {
          key: 'eye-injury-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.incurralPeriod)}
              disabled
              required
              id={'accEye.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'eye-injury-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.benefitAmount))}
              disabled
              required
              adornment="money"
              id={'accEye.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'eye-injury-row-2'},
      items: [
        {
          key: 'eye-injury-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCEyeInjuryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.maxBenefitNumber)}
              disabled
              required
              id={'accEye.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'eye-injury-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCEyeInjuryBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.timePeriodCd)}
              disabled
              required
              id={'accEye.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AccidentSurgeriesBlock = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'accident-surgeries-units-row-1'},
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              required
              disabled
              options={unitsOptions}
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccAccidentSurgeries)}
              id={propertyPaths.unitsOfAccAccidentSurgeries}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];
  const accidentSurgeries: ConditionalSection[] = [
    {
      Component: MiscellaneousOutpatientSurgery,
      key: 'MiscellaneousOutoatientSurgery',
      label: 'MISCELLANEOUS OUTPATIENT SURGERY',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isMiscellaneousOutpatientSurgeryApplied),
    },
    {
      Component: OpenAbdominalOrThoracicSurgery,
      key: 'OpenAbdominalOrThoracicSurgery',
      label: 'OPEN ABDOMINAL OR THORACIC SURGERY',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isOpenAbdominalOrThoracicSurgeryApplied),
    },
    {
      Component: LigamentRotatorcuffTendonKneecartilage,
      key: 'LigamentRotatorcuffTendonKneecartilage',
      label: 'LIGAMENT, ROTATOR CUFF, TENDON, OR KNEE CARTILAGE',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isTendonLigamentRotatorCuffApplied),
    },
    {
      Component: GeneralAnesthesia,
      key: 'GeneralAnesthesia',
      label: 'GENERAL ANESTHESIA',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isGeneralAnesthesiaApplied),
    },
    {
      Component: RupturedOrHerniatedDiscSurgery,
      key: 'RupturedOrHerniatedDiscSurgery',
      label: 'RUPTURED OR HERNIATED DISC SURGERY',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isRupturedDiscApplied),
    },
    {
      Component: BloodPlasmaPlatelets,
      key: 'BloodPlasmaPlatelets',
      label: 'BLOOD, PLASMA OR PLATELETS',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isBloodPlasmaPlateletsApplied),
    },
    {
      Component: EyeInjury,
      key: 'EyeInjury',
      label: 'EYE INJURY',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isEyeInjuryApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Treatment and Urgent Care - Accident Surgeries" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={accidentSurgeries} />
      </Grid>
    </FormWrapper>
  );
};
